import React from 'react';
import AppTooltip from '../../atomic/tooltip/AppTooltip';
import './priceplan.scss';

const CheckIcon = () => (
  <svg width='6' height='5' viewBox='0 0 10 8' fill='none' aria-hidden='true'>
    <path
      d='M1 4L3.5 6.5L9 1'
      stroke='white'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CrossIcon = () => (
  <svg width='5' height='5' viewBox='0 0 8 8' fill='none' aria-hidden='true'>
    <path d='M1 1L7 7M7 1L1 7' stroke='white' strokeWidth='1.6' strokeLinecap='round' />
  </svg>
);

const FeatureIcon = ({ isEnabled }) => (
  <span
    className={`plan-feature-item__icon ${
      isEnabled ? 'plan-feature-item__icon--enabled' : 'plan-feature-item__icon--disabled'
    }`}
    aria-hidden='true'
  >
    {isEnabled ? <CheckIcon /> : <CrossIcon />}
  </span>
);

export const PlanFeatureItem = ({ children, isEnabled, tooltip }) => (
  <div className='plan-feature-item'>
    <FeatureIcon isEnabled={isEnabled} />
    <span
      className={`plan-feature-item__text ${!isEnabled ? 'plan-feature-item__text--grey' : ''}`}
    >
      <span className='plan-feature-item__text-inner'>
        {children}
        {tooltip ? (
          <AppTooltip title={tooltip} className='app-tooltip__anchor--after-text' />
        ) : null}
      </span>
    </span>
  </div>
);

const PlatformFeesSection = ({ items, isEnabled = true, tooltip }) => (
  <div className='plan-platform-fees'>
    <div className='plan-platform-fees__header'>
      <FeatureIcon isEnabled={isEnabled} />
      <span className='plan-platform-fees__title-wrap'>
        <span
          className={`plan-platform-fees__title ${
            !isEnabled ? 'plan-feature-item__text--grey' : ''
          }`}
        >
          Commission
        </span>
        {tooltip ? (
          <AppTooltip
            title={tooltip}
            ariaLabel='Platform fees information'
            className='app-tooltip__anchor--after-text'
          />
        ) : null}
      </span>
    </div>
    <ul className='plan-platform-fees__list'>
      {items.map((item) => (
        <li key={item} className='plan-platform-fees__item'>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const featureLabel = (label, value) => (
  <>
    <span style={{ fontFamily: 'Inter Bold' }}>{label}</span>
    <span> {value}</span>
  </>
);

const BASIC_PLATFORM_FEES = [
  '0% on Delivery orders',
  '0% on Pickups',
  '3.9% + $0.35 / transaction',
  'Above $10,000/month: 10% fee on the amount over.',
];

const PLUS_PLATFORM_FEES = [
  '10% on Delivery Orders',
  '6% on Pickups',
  '6% for Customers You Invite',
  '2.9% + $0.30 / transaction',
];

const ENTERPRISE_PLATFORM_FEES = [
  '25% on Delivery orders',
  '6% on Pickups',
  '2.9% + $0.30 / transaction',
];

const BASIC_FEATURES = [
  {
    text: featureLabel('Account Fee:', '$0 for the first 6 months, then $29.99/month'),
    isEnabled: true,
    tooltip: '',
  },
  {
    text: featureLabel('Delivery Fee:', 'Set by restaurant, paid by the customer'),
    isEnabled: true,
    tooltip: 'You control the delivery fee charged to customers on your orders.',
  },
  {
    text: featureLabel(
      'Visibility:',
      'Top placement for customers who subscribe to your store or scan your QR code'
    ),
    isEnabled: true,
  },
  {
    text: featureLabel('Device + marketing materials:', '$3.99 weekly fee'),
    isEnabled: true,
  },
  {
    text: featureLabel('Referral earnings (up to 5%):', 'Not included'),
    isEnabled: false,
    tooltip: 'Earn a share of revenue when customers you refer place orders on the platform.',
  },
  {
    text: featureLabel('SavePass Club:', 'Not included'),
    isEnabled: false,
    tooltip: 'SavePass Club gives loyal customers $0 delivery fees and exclusive benefits.',
  },
];

const PLUS_FEATURES = [
  {
    text: featureLabel('Delivery Fee :', 'Set by Restaurant'),
    isEnabled: true,
    // tooltip: 'You control the delivery fee charged to customers on your orders.',
  },
  {
    text: featureLabel('Revenue Earned from Referrals (5%–10%) :', 'Yes'),
    isEnabled: true,
    tooltip: 'Earn a share of revenue when customers you refer place orders on the platform.',
  },
  {
    text: featureLabel('Visibility :', 'Top for Invited Customers'),
    isEnabled: true,
  },
  {
    text: featureLabel('Discoverable When Searched By :', 'Store Name'),
    isEnabled: true,
  },
  {
    text: featureLabel('Referral maintenance fee :', '100 and Above - $0.05'),
    isEnabled: true,
  },
  {
    text: featureLabel('OnePass Club :', 'No'),
    isEnabled: false,
    tooltip: 'OnePass Club gives loyal customers $0 delivery fees and exclusive benefits.',
  },
  {
    text: featureLabel('Professional Product Photography :', 'Yes'),
    isEnabled: true,
  },
  {
    text: featureLabel('Discounted Next-Day Meals :', '$25 Minimum Order'),
    isEnabled: true,
  },
  {
    text: featureLabel('Marketing on social platforms :', 'We create content (no upfront cost)'),
    isEnabled: false,
  },
  {
    text: featureLabel('Monthly Newsletter :', 'Free for Referral Members'),
    isEnabled: false,
  },
];

const ENTERPRISE_FEATURES = [
  {
    text: featureLabel('Account Fee:', '$0'),
    isEnabled: true,
    tooltip: '',
  },
  {
    text: featureLabel('Delivery Fee:', 'Low fee paid by the customer; $0 for SavePass members'),
    isEnabled: true,
    tooltip:
      "With Partner Delivery, SingleCombo handles delivery for you — so customers pay a lower fee, and you don't have to manage drivers",
  },
  {
    text: featureLabel(
      'Visibility:',
      'Top placement for subscribed/scanned customers, plus Neighborhood Favorite ranking based on subscriber count'
    ),
    isEnabled: true,
  },

  {
    text: featureLabel('Device + marketing materials:', '$2.99 weekly fee'),
    isEnabled: true,
  },

  {
    text: featureLabel('Referral earnings (up to 5%):', 'Included'),
    isEnabled: true,
    tooltip: 'Earn a share of revenue when customers you refer place orders on the platform.',
  },
  {
    text: featureLabel('SavePass Club:', 'Included'),
    isEnabled: true,
    tooltip: 'SavePass Club gives loyal customers $0 delivery fees and exclusive benefits.',
  },
];

const PLAN_CONFIG = {
  basic: {
    platformFees: {
      items: BASIC_PLATFORM_FEES,
      tooltip:
        'Commission apply to delivery orders, pickups. Card processing is billed separately at 3.9% + $0.35 per transaction.',
    },
    features: BASIC_FEATURES,
  },
  plus: {
    platformFees: {
      items: PLUS_PLATFORM_FEES,
      tooltip:
        'Same platform fee structure as Basic, with additional plan benefits unlocked. Card processing remains 2.9% + $0.30 per transaction.',
    },
    features: PLUS_FEATURES,
  },
  enterprise: {
    platformFees: {
      items: ENTERPRISE_PLATFORM_FEES,
      tooltip:
        'Enterprise uses partner delivery with higher delivery and invite commission rates. Card processing is 2.9% + $0.30 per transaction.',
    },
    features: ENTERPRISE_FEATURES,
  },
};

export const PlanFeatureList = ({ plan }) => {
  const config = PLAN_CONFIG[plan] ?? PLAN_CONFIG.basic;

  return (
    <>
      <PlatformFeesSection
        items={config.platformFees.items}
        isEnabled
        tooltip={config.platformFees.tooltip}
      />
      <div className='plan-feature-list'>
        {config.features.map((feature, index) => (
          <PlanFeatureItem key={index} isEnabled={feature.isEnabled} tooltip={feature.tooltip}>
            {feature.text}
          </PlanFeatureItem>
        ))}
      </div>
    </>
  );
};
